export type SessionAttributionProps = {
  session_acquisition_source?: string;
  session_acquisition_channel?: string;
  guide_landing_path?: string;
  guide_page_segment?: string;
  guide_source_task?: string;
  guide_cta_id?: string;
  guide_cta_position?: string;
  guide_component?: string;
  facility_slug?: string;
};

type StoredSessionAttribution = {
  captured_at: number;
  session_attribution: SessionAttributionProps;
};

const STORAGE_KEY = "pm_session_attribution";
const TTL_MS = 24 * 60 * 60 * 1000;
const GUIDE_FIELDS = [
  "guide_landing_path",
  "guide_page_segment",
  "guide_source_task",
  "guide_cta_id",
  "guide_cta_position",
  "guide_component",
  "facility_slug",
] as const;
// The fields whose presence marks a guides handoff. facility_slug is NOT one of
// them: it is also a plain prefill param on non-guides links to /search, so it
// must never by itself manufacture guides attribution.
const GUIDE_TRIGGER_FIELDS = GUIDE_FIELDS.filter((field) => field !== "facility_slug");
const ATTRIBUTION_PAYLOAD_FIELDS = [
  "session_acquisition_source",
  "session_acquisition_channel",
  ...GUIDE_FIELDS,
  "session_attribution",
  "metrics_context",
] as const;
const ATTRIBUTION_KEYS = new Set<string>(ATTRIBUTION_PAYLOAD_FIELDS);

const GUIDE_PAGE_SEGMENTS = [
  "article",
  "facility_article",
  "facility_inmate_search",
  "facility_intent",
  "facility_mail_photos",
  "facility_root",
  "facility_search",
  "facility_send_money",
  "facility_visitation",
  "global_inmate_search",
  "guides_home",
  "guides_search",
  "state_facility_directory",
  "state_hub",
  "state_topic_section",
  "topic_hub",
  "topic_section",
  "topics_index",
  "visitation",
  "send_money",
] as const;

const GUIDE_SOURCE_TASKS = [
  "contact",
  "facility_root",
  "inmate_search",
  "mail_photos",
  "send_money",
  "visitation",
] as const;

const GUIDE_CTA_POSITIONS = [
  "article_main",
  "article_related",
  "compact",
  "desktop_pill",
  "home_main",
  "inline",
  "intent_main",
  "intent_related",
  "landing_facility_search",
  "landing_primary",
  "main",
  "mobile_card",
  "root_bottom_inline",
  "root_main",
  "search_box",
  "search_page_main",
  "search_page_primary",
  "state_facility_directory_main",
  "state_facility_directory_search",
  "state_hub_main",
  "state_hub_search",
  "state_topic_section_main",
  "topic_hub_main",
  "topic_hub_search",
  "topic_section_main",
  "topics_index_search",
] as const;

const GUIDE_COMPONENTS = [
  "facility_hero",
  "facility_search",
  "facility_task_links",
  "facility_topic_nav",
  "inmate_search_card",
  "inmate_search_cta",
] as const;

const GUIDE_TOKEN_VALUES: Record<string, readonly string[]> = {
  guide_page_segment: GUIDE_PAGE_SEGMENTS,
  guide_source_task: GUIDE_SOURCE_TASKS,
  guide_cta_position: GUIDE_CTA_POSITIONS,
  guide_component: GUIDE_COMPONENTS,
};

const GUIDE_CTA_IDS = [
  "inmate_search_card_root_main",
  "inmate_search_card-facility_article-inmate_search-article_main",
  "inmate_search_card-facility_inmate_search-inmate_search-intent_main",
  "inmate_search_card-facility_mail_photos-inmate_search-intent_main",
  "inmate_search_card-facility_root-inmate_search-root_main",
  "inmate_search_card-facility_root-inmate_search-root_main-inmate-search",
  "inmate_search_card-facility_send_money-inmate_search-intent_main",
  "inmate_search_card-facility_visitation-inmate_search-intent_main",
  "inmate_search_card-global_inmate_search-inmate_search-landing_primary",
  "inmate_search_card-guides_home-inmate_search-home_main",
  "inmate_search_card-guides_search-inmate_search-search_page_main",
  "inmate_search_card-state_facility_directory-inmate_search-state_facility_directory_main",
  "inmate_search_card-state_hub-inmate_search-state_hub_main",
  "inmate_search_card-state_topic_section-inmate_search-state_topic_section_main",
  "inmate_search_card-topic_hub-inmate_search-topic_hub_main",
  "inmate_search_card-topic_section-inmate_search-topic_section_main",
];

const GUIDE_PATH_STATIC = [
  "/guides",
  "/guides/",
  "/guides/about/",
  "/guides/search/",
  "/guides/inmate-search/",
  "/guides/topics/",
];

const GUIDE_PATH_INTENTS = ["inmate-search", "visitation", "send-money", "send-photos", "contact"];

const GUIDE_PATH_TEMPLATES = [
  "/guides/facility/:facility/",
  "/guides/facility/:facility/articles/:article/",
  "/guides/tag/:tag/",
  "/guides/tag/:tag/facilities/",
  "/guides/tag/:tag/:article/",
  "/guides/topics/:topic/",
  "/guides/topics/:topic/:article/",
  ...GUIDE_PATH_INTENTS.map((intent) => `/guides/facility/:facility/${intent}/`),
];

const cleanGuideToken = (value: unknown, maxLength = 120): string | null => {
  if (typeof value !== "string") return null;
  const token = value.trim().toLowerCase();
  if (token.length > maxLength) return null;
  if (!token || !/^[a-z0-9][a-z0-9_-]{0,119}$/.test(token)) return null;
  return token;
};

const cleanKnownGuideToken = (field: string, value: unknown): string | null => {
  const token = cleanGuideToken(value);
  if (!token) return null;
  const allowedValues = GUIDE_TOKEN_VALUES[field];
  return allowedValues && allowedValues.indexOf(token) >= 0 ? token : null;
};

const cleanGuideCtaId = (value: unknown): string | null => {
  const token = cleanGuideToken(value);
  if (!token) return null;
  return GUIDE_CTA_IDS.indexOf(token) >= 0 ? token : null;
};

const safeGuideSlug = (value: string | undefined, maxLength = 80): boolean =>
  !!value && value.length <= maxLength && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

// The landing path is reduced to its route pattern; the facility slug is what
// makes a facility page identifiable again. Slug-shaped only.
const cleanGuideSlug = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const slug = value.trim().toLowerCase();
  return safeGuideSlug(slug) ? slug : null;
};

const cleanGuideField = (field: string, value: unknown): string | null => {
  if (field === "guide_landing_path") return cleanPath(value);
  if (field === "guide_cta_id") return cleanGuideCtaId(value);
  if (field === "facility_slug") return cleanGuideSlug(value);
  return cleanKnownGuideToken(field, value);
};

const canonicalGuidePath = (path: string): string | null => {
  if (path.indexOf("//") >= 0) return null;
  if (path === "/guides") return "/guides/";
  if (GUIDE_PATH_STATIC.indexOf(path) >= 0) return path;
  if (GUIDE_PATH_TEMPLATES.indexOf(path) >= 0) return path;

  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "guides") return null;

  if (parts[1] === "facility") {
    if (!safeGuideSlug(parts[2])) return null;
    if (parts.length === 3) return "/guides/facility/:facility/";
    if (parts.length === 4 && GUIDE_PATH_INTENTS.indexOf(parts[3]) >= 0) {
      return `/guides/facility/:facility/${parts[3]}/`;
    }
    if (parts.length === 5 && parts[3] === "articles" && safeGuideSlug(parts[4])) {
      return "/guides/facility/:facility/articles/:article/";
    }
  } else if (parts[1] === "tag") {
    if (!safeGuideSlug(parts[2], 2)) return null;
    if (parts.length === 3) return "/guides/tag/:tag/";
    if (parts.length === 4 && parts[3] === "facilities") return "/guides/tag/:tag/facilities/";
    if (parts.length === 4 && safeGuideSlug(parts[3])) return "/guides/tag/:tag/:article/";
  } else if (parts[1] === "topics") {
    if (parts.length === 3 && safeGuideSlug(parts[2])) return "/guides/topics/:topic/";
    if (parts.length === 4 && safeGuideSlug(parts[2]) && safeGuideSlug(parts[3])) {
      return "/guides/topics/:topic/:article/";
    }
  }

  return null;
};

const cleanPath = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  let path: string | null;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      path = new URL(trimmed).pathname;
    } else {
      path = trimmed.split(/[?#]/)[0];
    }
  } catch {
    return null;
  }
  path = path.split(/[?#]|%(?:25)*(?:3f|23)/i)[0];
  if (!path || path.charAt(0) !== "/") return null;
  const canonicalPath = canonicalGuidePath(path);
  if (!canonicalPath) return null;
  return canonicalPath.slice(0, 512);
};

const sameProps = (left: SessionAttributionProps, right: SessionAttributionProps): boolean => {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (left[key as keyof SessionAttributionProps] !== right[key as keyof SessionAttributionProps]) return false;
  }
  return true;
};

const cleanProps = (source: Record<string, unknown>): SessionAttributionProps => {
  const props: SessionAttributionProps = {};
  if (cleanGuideToken(source.session_acquisition_source) === "guides") {
    props.session_acquisition_source = "guides";
  }
  if (props.session_acquisition_source === "guides") {
    props.session_acquisition_channel = "unknown";
  }
  const landingPath = cleanPath(source.guide_landing_path);
  if (landingPath) props.guide_landing_path = landingPath;
  for (const field of GUIDE_FIELDS) {
    if (field === "guide_landing_path") continue;
    const value = cleanGuideField(field, source[field]);
    if (value) props[field] = value;
  }
  return props;
};

const payloadKey = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").replace(/-/g, "_").toLowerCase();

const isAttributionKey = (key: string): boolean =>
  ATTRIBUTION_KEYS.has(payloadKey(key));

const stripAttribution = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripAttribution);
  if (!value || typeof value !== "object") return value;

  const cleaned: Record<string, unknown> = {};
  for (const [key, childValue] of Object.entries(value as Record<string, unknown>)) {
    if (isAttributionKey(key)) continue;
    cleaned[key] = stripAttribution(childValue);
  }
  return cleaned;
};

const readStored = (): StoredSessionAttribution | null => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const capturedAt = Number(parsed?.captured_at);
    const source = parsed?.session_attribution;
    if (!capturedAt || !source || typeof source !== "object") return null;
    const props = cleanProps(source as Record<string, unknown>);
    if (props.session_acquisition_source !== "guides") return null;
    return {
      captured_at: capturedAt,
      session_attribution: props,
    };
  } catch {
    return null;
  }
};

const writeStored = (props: SessionAttributionProps): void => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const now = Date.now();
    const stored = readStored();
    const capturedAt =
      stored &&
      now - stored.captured_at <= TTL_MS &&
      sameProps(stored.session_attribution, props)
        ? stored.captured_at
        : now;
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        captured_at: capturedAt,
        session_attribution: props,
      }),
    );
  } catch {
    // Session attribution is best-effort and must not block app flows.
  }
};

const clearStored = (): void => {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};

export const captureSessionAttributionFromUrl = (): SessionAttributionProps => {
  try {
    if (typeof window === "undefined" || !window.location) return getSessionAttributionProps();
    const params = new URL(window.location.href).searchParams;
    const source = cleanGuideToken(params.get("source"));
    const hasGuideParam = GUIDE_TRIGGER_FIELDS.some((field) => !!cleanGuideField(field, params.get(field)));
    if (source !== "guides" && !hasGuideParam) return getSessionAttributionProps();

    const props: SessionAttributionProps = {
      session_acquisition_source: "guides",
      session_acquisition_channel: "unknown",
    };
    const landingPath = cleanPath(params.get("guide_landing_path"));
    if (landingPath) props.guide_landing_path = landingPath;
    for (const field of GUIDE_FIELDS) {
      if (field === "guide_landing_path") continue;
      const value = cleanGuideField(field, params.get(field));
      if (value) props[field] = value;
    }
    writeStored(props);
    return props;
  } catch {
    return getSessionAttributionProps();
  }
};

export const getSessionAttributionProps = (): SessionAttributionProps => {
  const stored = readStored();
  if (!stored) return {};
  if (Date.now() - stored.captured_at > TTL_MS) {
    clearStored();
    return {};
  }
  return { ...stored.session_attribution };
};

export const withSessionAttributionProps = <T extends Record<string, unknown>>(payload: T): T & SessionAttributionProps => {
  const props = getSessionAttributionProps();
  return { ...withoutSessionAttributionProps(payload), ...props };
};

export const withoutSessionAttributionProps = <T extends Record<string, unknown>>(payload: T): T => {
  return stripAttribution(payload) as T;
};

export const sessionAttributionMetricsContext = (): Record<string, unknown> => {
  const props = getSessionAttributionProps();
  if (!props.session_acquisition_source) return {};
  return {
    metrics_context: {
      session_attribution: props,
    },
  };
};

export const withSessionAttributionContext = <T extends Record<string, unknown>>(body: T): T & Record<string, unknown> => ({
  ...body,
  ...sessionAttributionMetricsContext(),
});
